/**
 * System route (§4.7).
 *
 * Three tabs for v1:
 *   - Services  — every systemd unit Mission Control tracks, with live
 *                 state + start/stop/restart buttons (wired to the
 *                 generic `/agents/{name}/{action}` endpoints when the
 *                 service name begins with `agent-`).
 *   - Audit log — paginated table of mutations, filterable by agent
 *                 and action type; CSV export button downloads the
 *                 filtered slice.
 *   - Backups   — list of config + memory backups, create + restore.
 *
 * Metrics, Upgrade, and Raw-config tabs are intentionally deferred to
 * a follow-up PR — the stubs still render something so the tab bar
 * doesn't dead-end.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, RefreshCw, ServerCog, Network, CheckCircle2, XCircle } from 'lucide-react';
import { api } from '@/api/client';
import { configApi, type ConfigBackup } from '@/api/config';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import { StepUpDialog } from '@/components/StepUpDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { GlobalOverlays } from '@/components/GlobalOverlays';
import { formatBytes, formatDate, formatRelative } from '@/lib/format';
import { ApiError } from '@/api/client';

export default function System() {
  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">System</h1>
        <p className="text-sm text-muted-foreground">
          Services, audit log, and backups for this AgentHub host.
        </p>
      </div>
      <Tabs defaultValue="services">
        <TabsList>
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="cluster">Cluster</TabsTrigger>
          <TabsTrigger value="audit">Audit log</TabsTrigger>
          <TabsTrigger value="backups">Backups</TabsTrigger>
        </TabsList>
        <TabsContent value="services" className="mt-4">
          <ServicesTab />
        </TabsContent>
        <TabsContent value="cluster" className="mt-4">
          <ClusterTab />
        </TabsContent>
        <TabsContent value="audit" className="mt-4">
          <AuditLogTab />
        </TabsContent>
        <TabsContent value="backups" className="mt-4">
          <BackupsTab />
        </TabsContent>
      </Tabs>
      <GlobalOverlays />
    </div>
  );
}

/* ----- Services tab ----- */

interface ServiceEntry {
  name: string;
  unit: string;
  state: string;
  sub_state?: string;
  pid?: number;
  memory_bytes?: number;
  started_at?: string;
}

function ServicesTab() {
  const query = useQuery({
    queryKey: ['system', 'services'],
    queryFn: () => api<{ items: ServiceEntry[] }>('/system/services'),
    refetchInterval: 10_000,
    retry: false,
  });

  if (query.isLoading) return <LoadingSkeleton className="h-40 w-full" />;
  if (query.isError) {
    return (
      <p className="text-sm text-destructive">
        Could not load services: {(query.error as Error).message}
      </p>
    );
  }

  const items = query.data?.items ?? [];
  if (items.length === 0) {
    return <EmptyState icon={ServerCog} title="No services reported" />;
  }

  return (
    <div className="rounded-md border overflow-hidden">
      <table className="w-full">
        <thead className="bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="py-2 px-4 font-semibold">Service</th>
            <th className="py-2 px-4 font-semibold">State</th>
            <th className="py-2 px-4 font-semibold">PID</th>
            <th className="py-2 px-4 font-semibold text-right">Memory</th>
            <th className="py-2 px-4 font-semibold">Started</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.unit} className="border-b last:border-b-0">
              <td className="py-3 px-4">
                <div className="font-medium">{s.name}</div>
                <div className="font-mono text-xs text-muted-foreground">{s.unit}</div>
              </td>
              <td className="py-3 px-4 text-sm">
                <span
                  className={
                    s.state === 'active'
                      ? 'text-success'
                      : s.state === 'failed'
                      ? 'text-destructive'
                      : 'text-muted-foreground'
                  }
                >
                  {s.state}
                  {s.sub_state && ` (${s.sub_state})`}
                </span>
              </td>
              <td className="py-3 px-4 font-mono text-xs">{s.pid ?? '—'}</td>
              <td className="py-3 px-4 text-right text-xs">
                {s.memory_bytes ? formatBytes(s.memory_bytes) : '—'}
              </td>
              <td className="py-3 px-4 text-xs text-muted-foreground">
                {s.started_at ? formatRelative(s.started_at) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ----- Cluster tab (claude-peers + future shared MCPs) ----- */

interface ClusterAgentRow {
  name: string;
  peers_enabled: boolean;
  registered: boolean;
  peer_id?: string | null;
}

interface ClusterStatus {
  broker_active: boolean;
  broker_pid?: number | null;
  broker_listen: string;
  peer_count: number;
  agents: ClusterAgentRow[];
}

function ClusterTab() {
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmRestart, setConfirmRestart] = useState<string[] | null>(null);

  const query = useQuery<ClusterStatus>({
    queryKey: ['system', 'cluster'],
    queryFn: () => api<ClusterStatus>('/system/cluster'),
    refetchInterval: 5_000,
    retry: false,
  });

  const bulk = useMutation({
    mutationFn: (enabled: boolean) =>
      api<{ ok: boolean; changed: string[] }>('/system/cluster/peers-bulk', {
        method: 'POST',
        body: { enabled },
      }),
    onSuccess: (data, enabled) => {
      qc.invalidateQueries({ queryKey: ['system', 'cluster'] });
      setNotice(
        data.changed.length === 0
          ? `No changes needed — already ${enabled ? 'enabled' : 'disabled'} for all.`
          : `${enabled ? 'Enabled' : 'Disabled'} for: ${data.changed.join(', ')}. Click "Restart pending" to apply.`,
      );
    },
    onError: (err) => {
      setNotice(err instanceof ApiError ? err.detail ?? err.title : 'Bulk toggle failed');
    },
  });

  // Restart only the agents whose config drifted from their live state.
  const restartPending = useMutation({
    mutationFn: async (names: string[]) => {
      const results = await Promise.allSettled(
        names.map((name) =>
          api<{ ok: boolean }>(`/agents/${encodeURIComponent(name)}/restart`, { method: 'POST' }),
        ),
      );
      return results;
    },
    onSuccess: (_, names) => {
      qc.invalidateQueries({ queryKey: ['system', 'cluster'] });
      setNotice(`Restarted ${names.length} agent${names.length === 1 ? '' : 's'}: ${names.join(', ')}`);
    },
    onError: (err) => {
      setNotice(err instanceof ApiError ? err.detail ?? err.title : 'Restart failed');
    },
  });

  async function handleBulk(enabled: boolean) {
    setPending(true);
    setNotice(null);
    try {
      await bulk.mutateAsync(enabled);
    } finally {
      setPending(false);
    }
  }

  async function handleRestartPending(names: string[]) {
    setPending(true);
    setNotice(null);
    try {
      await restartPending.mutateAsync(names);
    } finally {
      setPending(false);
    }
  }

  if (query.isLoading) return <LoadingSkeleton className="h-40 w-full" />;
  if (query.isError) {
    return (
      <p className="text-sm text-destructive">
        Could not load cluster status: {(query.error as Error).message}
      </p>
    );
  }

  const data = query.data!;
  const enabledCount = data.agents.filter((a) => a.peers_enabled).length;
  // An agent has pending changes when its configured state differs from live.
  const pendingAgents = data.agents.filter(
    (a) => a.peers_enabled !== a.registered,
  );

  return (
    <div className="space-y-5">
      {/* Header card with broker status + bulk toggles */}
      <div className="rounded-md border bg-card p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Network className="h-6 w-6 mt-0.5 text-muted-foreground shrink-0" />
            <div>
              <h3 className="text-base font-semibold flex items-center gap-2">
                Inter-agent messaging
                <span
                  className={
                    data.broker_active
                      ? 'inline-flex items-center gap-1 rounded-md border border-success/30 bg-success/10 px-2 py-0.5 text-xs font-medium text-success'
                      : 'inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive'
                  }
                >
                  {data.broker_active ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                  Broker {data.broker_active ? 'online' : 'down'}
                </span>
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                claude-peers MCP — {data.peer_count} of {data.agents.length} agents connected.
                {' '}Broker on <code className="font-mono text-xs">{data.broker_listen}</code>
                {data.broker_pid ? ` (pid ${data.broker_pid})` : ''}.
              </p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulk(false)}
              disabled={pending || enabledCount === 0}
            >
              Disable for all
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => handleBulk(true)}
              disabled={pending || enabledCount === data.agents.length}
            >
              {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Enable for all
            </Button>
          </div>
        </div>

        {notice && (
          <div className="mt-3 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm">
            {notice}
          </div>
        )}
      </div>

      {/* Pending-restart banner */}
      {pendingAgents.length > 0 && (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-4 py-3 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">
              {pendingAgents.length} agent{pendingAgents.length === 1 ? '' : 's'} need{pendingAgents.length === 1 ? 's' : ''} restart
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Config changed but the running process still has its old MCP state:
              {' '}
              <code className="font-mono text-xs">{pendingAgents.map((a) => a.name).join(', ')}</code>
            </p>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={() => setConfirmRestart(pendingAgents.map((a) => a.name))}
            disabled={pending}
            className="shrink-0"
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Restart pending
          </Button>
        </div>
      )}

      {/* Restart confirmation — warns the user any in-flight Discord/peer
          conversation will be interrupted while the agent reboots. */}
      <ConfirmDialog
        open={confirmRestart !== null}
        onOpenChange={(o) => !o && setConfirmRestart(null)}
        title={`Restart ${confirmRestart?.length ?? 0} agent${(confirmRestart?.length ?? 0) === 1 ? '' : 's'}?`}
        description={
          confirmRestart
            ? `${confirmRestart.join(', ')} will restart now to apply the new MCP config. ` +
              `This interrupts any in-flight Discord conversations or peer messages. ` +
              `Each restart takes ~5 seconds. Memory and session history are preserved.`
            : ''
        }
        confirmLabel="Restart now"
        onConfirm={() => {
          if (!confirmRestart) return;
          handleRestartPending(confirmRestart);
          setConfirmRestart(null);
        }}
      />

      {/* Per-agent table */}
      <div className="rounded-md border overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="py-2 px-4 font-semibold">Agent</th>
              <th className="py-2 px-4 font-semibold">Configured</th>
              <th className="py-2 px-4 font-semibold">Live</th>
              <th className="py-2 px-4 font-semibold">Peer ID</th>
            </tr>
          </thead>
          <tbody>
            {data.agents.map((a) => (
              <tr key={a.name} className="border-b last:border-b-0">
                <td className="py-3 px-4 font-medium">{a.name}</td>
                <td className="py-3 px-4 text-sm">
                  {a.peers_enabled ? (
                    <span className="inline-flex items-center gap-1 text-success">
                      <CheckCircle2 className="h-3.5 w-3.5" /> enabled
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <XCircle className="h-3.5 w-3.5" /> disabled
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-sm">
                  {a.registered ? (
                    <span className="inline-flex items-center gap-1 text-success">
                      <CheckCircle2 className="h-3.5 w-3.5" /> connected
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <XCircle className="h-3.5 w-3.5" /> {a.peers_enabled ? 'not registered' : '—'}
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-xs font-mono text-muted-foreground">
                  {a.peer_id ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Bulk-toggling rewrites each agent's <code className="font-mono">.mcp.json</code>. Changes take effect on the next agent restart.
      </p>
    </div>
  );
}

/* ----- Audit log tab ----- */

interface AuditEntry {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  target?: string;
  success: boolean;
  detail?: string;
}

function AuditLogTab() {
  const [agentFilter, setAgentFilter] = useState('');
  const query = useQuery({
    queryKey: ['system', 'audit', agentFilter],
    queryFn: () => {
      const qs = agentFilter ? `?target=${encodeURIComponent(agentFilter)}` : '';
      return api<{ items: AuditEntry[]; next_cursor?: string }>(`/system/audit-log${qs}`);
    },
    retry: false,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="Filter by agent…"
          value={agentFilter}
          onChange={(e) => setAgentFilter(e.target.value)}
          className="flex h-9 w-64 rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
        <Button variant="outline" size="sm" onClick={() => query.refetch()}>
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>
      {query.isLoading && <LoadingSkeleton className="h-40 w-full" />}
      {query.isError && (
        <p className="text-sm text-destructive">
          Could not load audit log: {(query.error as Error).message}
        </p>
      )}
      {query.data && query.data.items.length === 0 && (
        <EmptyState title="No audit entries" description="Nothing to show for that filter." />
      )}
      {query.data && query.data.items.length > 0 && (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="py-2 px-4 font-semibold">Time</th>
                <th className="py-2 px-4 font-semibold">User</th>
                <th className="py-2 px-4 font-semibold">Action</th>
                <th className="py-2 px-4 font-semibold">Target</th>
                <th className="py-2 px-4 font-semibold">Result</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((e) => (
                <tr key={e.id} className="border-b last:border-b-0 align-top">
                  <td className="py-2 px-4 text-xs text-muted-foreground">{formatDate(e.timestamp)}</td>
                  <td className="py-2 px-4 text-sm">{e.user}</td>
                  <td className="py-2 px-4 text-sm font-mono">{e.action}</td>
                  <td className="py-2 px-4 text-sm">{e.target ?? '—'}</td>
                  <td className="py-2 px-4 text-sm">
                    <span className={e.success ? 'text-success' : 'text-destructive'}>
                      {e.success ? 'success' : 'failure'}
                    </span>
                    {e.detail && (
                      <div className="text-xs text-muted-foreground line-clamp-2">{e.detail}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ----- Backups tab ----- */

function BackupsTab() {
  const query = useQuery({
    queryKey: ['config', 'backups'],
    queryFn: () => configApi.listBackups(),
    retry: false,
  });

  const [pendingRestore, setPendingRestore] = useState<ConfigBackup | null>(null);
  const [stepUpOpen, setStepUpOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  async function handleCreateBackup() {
    setCreating(true);
    setError(null);
    try {
      await configApi.createBackup();
      query.refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create backup');
    } finally {
      setCreating(false);
    }
  }

  function handleConfirmRestore() {
    setStepUpOpen(true);
  }

  async function handleStepUpSuccess(token: string) {
    if (!pendingRestore) return;
    try {
      await configApi.restoreBackup(pendingRestore.id, token);
      setPendingRestore(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to restore backup');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Automatic backups are created before every deploy. Manual backups are tagged{' '}
          <code>manual</code>.
        </p>
        <Button onClick={handleCreateBackup} disabled={creating}>
          {creating && <Loader2 className="h-4 w-4 animate-spin" />}
          Create backup
        </Button>
      </div>
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      {query.isLoading && <LoadingSkeleton className="h-40 w-full" />}
      {query.isError && (
        <p className="text-sm text-destructive">
          Could not load backups: {(query.error as Error).message}
        </p>
      )}
      {query.data && query.data.items.length === 0 && (
        <EmptyState title="No backups yet" description="Run a deploy to generate one." />
      )}
      {query.data && query.data.items.length > 0 && (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="py-2 px-4 font-semibold">Created</th>
                <th className="py-2 px-4 font-semibold">Kind</th>
                <th className="py-2 px-4 font-semibold text-right">Size</th>
                <th className="py-2 px-4 font-semibold">Note</th>
                <th className="py-2 px-4" />
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((b) => (
                <tr key={b.id} className="border-b last:border-b-0">
                  <td className="py-2 px-4 text-sm">{formatDate(b.created_at)}</td>
                  <td className="py-2 px-4 text-sm">{b.kind}</td>
                  <td className="py-2 px-4 text-right text-xs text-muted-foreground">
                    {formatBytes(b.size_bytes)}
                  </td>
                  <td className="py-2 px-4 text-xs text-muted-foreground">{b.note ?? '—'}</td>
                  <td className="py-2 px-4 text-right flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                    >
                      <a href={`/api/config/backups/${b.id}/download`}>
                        <Download className="h-4 w-4" />
                        Download
                      </a>
                    </Button>
                    <Button size="sm" onClick={() => setPendingRestore(b)}>
                      Restore
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingRestore}
        onOpenChange={(open) => !open && setPendingRestore(null)}
        title="Restore backup"
        description={`This will replace your current config with the backup from ${
          pendingRestore ? formatDate(pendingRestore.created_at) : ''
        }. All running agents will be restarted.`}
        variant="destructive"
        confirmLabel="Restore"
        onConfirm={handleConfirmRestore}
      />
      <StepUpDialog
        open={stepUpOpen}
        onOpenChange={setStepUpOpen}
        onSuccess={handleStepUpSuccess}
        title="Confirm restore"
      />
    </div>
  );
}
